import {
  accountErrorFragment,
  staffErrorFragment
} from "@saleor/fragments/errors";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import { staffMemberDetailsFragment } from "@saleor/fragments/staff";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import {
  AccountSetNewPassword,
  AccountSetNewPasswordVariables
} from "./types/AccountSetNewPassword";
import {
  ChangeStaffPassword,
  ChangeStaffPasswordVariables
} from "./types/ChangeStaffPassword";
import {
  StaffAddressCreate,
  StaffAddressCreateVariables
} from "./types/StaffAddressCreate";
import {
  StaffAddressSetHome,
  StaffAddressSetHomeVariables
} from "./types/StaffAddressSetHome";
import { StaffAvatarDelete } from "./types/StaffAvatarDelete";
import {
  StaffAvatarUpdate,
  StaffAvatarUpdateVariables
} from "./types/StaffAvatarUpdate";
import {
  StaffMemberAdd,
  StaffMemberAddVariables
} from "./types/StaffMemberAdd";
import {
  StaffMemberDelete,
  StaffMemberDeleteVariables
} from "./types/StaffMemberDelete";
import {
  StaffMemberUpdate,
  StaffMemberUpdateVariables
} from "./types/StaffMemberUpdate";

const staffMemberAddMutation = gql`
  ${staffErrorFragment}
  ${staffMemberDetailsFragment}
  ${pageInfoFragment}
  mutation StaffMemberAdd(
    $input: StaffCreateInput!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    staffCreate(input: $input) {
      errors: staffErrors {
        ...StaffErrorFragment
      }
      user {
        ...StaffMemberDetailsFragment
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedStaffMemberAddMutation = TypedMutation<
  StaffMemberAdd,
  StaffMemberAddVariables
>(staffMemberAddMutation);

const staffMemberUpdateMutation = gql`
  ${staffErrorFragment}
  ${staffMemberDetailsFragment}
  ${pageInfoFragment}
  mutation StaffMemberUpdate(
    $id: ID!
    $input: StaffUpdateInput!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    staffUpdate(id: $id, input: $input) {
      errors: staffErrors {
        ...StaffErrorFragment
      }
      user {
        ...StaffMemberDetailsFragment
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedStaffMemberUpdateMutation = TypedMutation<
  StaffMemberUpdate,
  StaffMemberUpdateVariables
>(staffMemberUpdateMutation);

const staffMemberDeleteMutation = gql`
  ${staffErrorFragment}
  mutation StaffMemberDelete($id: ID!) {
    staffDelete(id: $id) {
      errors: staffErrors {
        ...StaffErrorFragment
      }
    }
  }
`;
export const TypedStaffMemberDeleteMutation = TypedMutation<
  StaffMemberDelete,
  StaffMemberDeleteVariables
>(staffMemberDeleteMutation);

const staffAvatarUpdateMutation = gql`
  ${accountErrorFragment}
  mutation StaffAvatarUpdate($image: Upload!) {
    userAvatarUpdate(image: $image) {
      errors: accountErrors {
        ...AccountErrorFragment
      }
      user {
        id
        avatar {
          url
        }
      }
    }
  }
`;
export const TypedStaffAvatarUpdateMutation = TypedMutation<
  StaffAvatarUpdate,
  StaffAvatarUpdateVariables
>(staffAvatarUpdateMutation);

const staffAvatarDeleteMutation = gql`
  ${accountErrorFragment}
  mutation StaffAvatarDelete {
    userAvatarDelete {
      errors: accountErrors {
        ...AccountErrorFragment
      }
      user {
        id
        avatar {
          url
        }
      }
    }
  }
`;
export const TypedStaffAvatarDeleteMutation = TypedMutation<
  StaffAvatarDelete,
  StaffMemberDeleteVariables
>(staffAvatarDeleteMutation);

const changeStaffPassword = gql`
  ${accountErrorFragment}
  mutation ChangeStaffPassword($newPassword: String!, $oldPassword: String!) {
    passwordChange(newPassword: $newPassword, oldPassword: $oldPassword) {
      errors: accountErrors {
        ...AccountErrorFragment
      }
    }
  }
`;
export const useChangeStaffPassword = makeMutation<
  ChangeStaffPassword,
  ChangeStaffPasswordVariables
>(changeStaffPassword);

const accountSetNewPassword = gql`
  ${accountErrorFragment}
  mutation AccountSetNewPassword($user: ID!, $newPassword: String!) {
    accountSetNewPassword(user: $user, newPassword: $newPassword) {
      errors: accountErrors {
        ...AccountErrorFragment
      }
      user {
        id
        email
      }
    }
  }
`;

export const useChangeOtherStaffPassword = makeMutation<
  AccountSetNewPassword,
  AccountSetNewPasswordVariables
>(accountSetNewPassword);

// export const useChangeOtherStaffPassword = makeMutation<

const staffAddressCreate = gql`
  mutation StaffAddressCreate($input: AddressInput!, $userId: ID!) {
    addressCreate(input: $input, userId: $userId) {
      accountErrors {
        field
        message
        code
      }
      user {
        id
      }
      address {
        id
        companyName
        streetBrgy
        city
        provinceCode
        postalCode
        country {
          code
          country
        }
        mapPlaceId
      }
    }
  }
`;
export const useStaffAddressCreate = makeMutation<
  StaffAddressCreate,
  StaffAddressCreateVariables
>(staffAddressCreate);

const staffAddressSetHome = gql`
  mutation StaffAddressSetHome($addressId: ID!, $userId: ID!) {
    addressSetHome(addressId: $addressId, userId: $userId) {
      user {
        id
        homeAddress {
          id
          companyName
          streetBrgy
          city
          provinceCode
          postalCode
          country {
            code
            country
          }
          mapPlaceId
        }
      }
    }
  }
`;
export const useStaffAddressSetHome = makeMutation<
  StaffAddressSetHome,
  StaffAddressSetHomeVariables
>(staffAddressSetHome);
